import { useTranslation } from "react-i18next";
import {
  EnumOrderDeliveryMethod,
  EnumOrderFulfillmentStatus,
  OrderByIdQuery,
} from "gql/generated";
import { ReservationInfoCard } from "./ReservationInfoCard";

type Order = NonNullable<OrderByIdQuery["orderById"]>;

/** Customer-facing delivery progress for merch orders (pickup or shipping). */
export const MerchDeliveryStatusCard = ({ order }: { order: Order }) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.DELIVERY_STATUS",
  });
  const delivery = order.delivery;
  if (!delivery) return null;

  const status = order.fulfillment?.status;
  const note = order.fulfillment?.note?.trim();
  const isShip = delivery.method === EnumOrderDeliveryMethod.Ship;
  const location = [delivery.pickupLocationName, delivery.pickupLocationAddress]
    .filter(Boolean)
    .join(", ");
  const tracking = [order.fulfillment?.carrier, order.fulfillment?.trackingNumber]
    .filter(Boolean)
    .join(" · ");

  const withNote = (text: string) => [text, note].filter(Boolean).join(" ");

  if (status === EnumOrderFulfillmentStatus.Shipped) {
    return (
      <ReservationInfoCard
        visual="success"
        title={t("SHIPPED_TITLE")}
        description={withNote(
          tracking ? t("SHIPPED_TRACKING", { tracking }) : t("SHIPPED"),
        )}
      />
    );
  }
  if (
    status === EnumOrderFulfillmentStatus.PickedUp ||
    status === EnumOrderFulfillmentStatus.Fulfilled
  ) {
    return (
      <ReservationInfoCard
        visual="success"
        title={t(isShip ? "DELIVERED_TITLE" : "PICKED_UP_TITLE")}
        description={withNote(t("COMPLETE"))}
      />
    );
  }
  if (status === EnumOrderFulfillmentStatus.Packed) {
    return (
      <ReservationInfoCard
        visual="accent.blue"
        title={t(isShip ? "PACKED_TITLE" : "READY_TITLE")}
        description={withNote(
          isShip ? t("PACKED") : t("READY", { location }),
        )}
      />
    );
  }
  return (
    <ReservationInfoCard
      visual="accent.blue"
      title={t(isShip ? "PREPARING_SHIP_TITLE" : "PREPARING_PICKUP_TITLE")}
      description={
        isShip ? t("PREPARING_SHIP") : t("PREPARING_PICKUP", { location })
      }
    />
  );
};
